import useLabels from "../../hooks/useLabels";
import SecondaryBar from "../SecondaryBar";
import {
  ConfirmationModal,
  CTA,
  InfoCard,
  OperationRecap,
  Tooltip,
} from "../Generic";
import {
  selectConfigs,
  selectDrugs,
  selectUnits,
  selectWarehouseByType,
} from "../../context/selectors";
import {
  getWarehouseByType,
  getDrugs,
  newWarehouseByType,
  deleteWarehouseByType,
  updateWarehouseByType,
  getUnits,
  newDrug,
  updateDrug,
  deleteDrug,
  updateDrugWarehouse,
  getOIVSubstances,
} from "../../services/utils";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addDrugItem,
  removeDrugItem,
  setDrugs,
  updateDrugItem,
} from "../../context/substances/drugsSlice";

import additives from "../../assets/icons/additives.png";
import adjuvants from "../../assets/icons/adjuvants.png";
import { SkeletonTableEdit } from "../Skeleton/Skeleton";
import { ROLES } from "../../constants/base";
import { useNavigate, useParams } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import TableEdit from "../TableEdit";
import { createColumnHelper } from "@tanstack/react-table";
import TableCell from "../TableEdit/TableCell";
import {
  addWarehouse,
  removeWarehouse,
  setWarehouse,
  updateWarehouse,
} from "../../context/warehouse/warehouseSlice";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import EditCell from "../TableEdit/EditCell";
import { INT_FLOAT_REGEX } from "../../constants/regex";
import "./styles/index.scss";
import { setUnits } from "../../context/units/unitsSlice";
import MultipleRowsCell from "../TableEdit/MultipleRowsCell";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getItem, setItem } from "../../services/LocalStorage";
import TabSelector from "../TabSelector";

const Warehouse = ({ isMobile }) => {
  // const { type: rawType } = useParams();
  const { type } = useParams();
  const navigate = useNavigate();
  const [getLabel] = useLabels();
  const { auth } = useAuth();
  const role = auth?.role || ROLES.GUEST;
  const substances = useSelector(selectDrugs);
  const units = useSelector(selectUnits);
  const configs = useSelector(selectConfigs);
  const [substancesLen, setSubstancesLen] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  // const [type, setType] = useState(rawType);
  const [isStock, setIsStock] = useState(type === "stock");
  const [isRemoveId, setIsRemoveId] = useState(-1);
  const [isModifyMode, setIsModifyMode] = useState(-1);
  const [activeTab, setActiveTab] = useState();
  const warehouseData = useSelector((state) => {
    if (["treatments", "stock"].includes(type))
      return state?.warehouse?.oiv || [];
    else if (type === "filters" && ["carton", "tangential", "housing"].includes(activeTab)) 
      return (selectWarehouseByType(type)(state))?.filter((item) => item?.type === activeTab?.toUpperCase?.());
    return selectWarehouseByType(type)(state);
  });
  const candlesData = useSelector(selectWarehouseByType("candles"));

  const tabs = [
    { name: getLabel("drugWarehouseProductPage"), anchor: "treatments" },
    { name: getLabel("drugWarehouseStockPage"), anchor: "stock" },
  ];

   const filterTabs = [
    { name: getLabel("filterWarehouseCartonsPage"), anchor: "carton" },
    { name: getLabel("filterWarehouseTangentialPage"), anchor: "tangential" },
    { name: getLabel("filterWarehouseHousingPage"), anchor: "housing" },
  ];

  const handleTabChange = (anchor) => {
    setActiveTab(anchor);
    setItem(`activeTab-${type === 'filters' ? 'filters' : 'warehouse'}`, anchor);
    // setType(anchor);
  };

  const getLastActiveTab = () => {
    try {
      const savedTab = getItem(`activeTab-${type === 'filters' ? 'filters' : 'warehouse'}`);

      if (savedTab && (type === "filters" ? filterTabs : tabs).some((tab) => tab.anchor === savedTab)) {
        setActiveTab(savedTab);
        // setType(savedTab);
        return savedTab;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  const axiosPrivate = useAxiosPrivate();
  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    setValue,
    getValues,
    setError,
    clearErrors,
    control,
  } = useForm({
    defaultValues: {
      is_co_formulated: false,
      min_stock: null,
      compositions: [
        {
          type: { value: null, label: "" },
          functional: { value: null, label: "" },
          substance: { value: null, label: "" },
          physical: { value: null, label: "" },
          origins: [],
          quantity: 0,
        },
      ],
      candles: [ { value: null, label: null } ]
    },
    mode: "onTouched",
  });

  const { batches } = useWatch({ control });

  const arrayFields = useFieldArray({
    control,
    name: "compositions",
  });

  const arrayFields2 = useFieldArray({
    control,
    name: "batches",
  });

  const arrayFields3 = useFieldArray({
    control,
    name: "candles",
  });

  const loadSubstances = async () => {
    const currentSubstances = await getDrugs(axiosPrivate);
    dispatch(setDrugs(currentSubstances));
  };

  const loadWarehouse = async () => {
    const currentData = await getWarehouseByType(axiosPrivate, type);
    // if (type === "filters" && (!warehouseData || warehouseData.length === 0)) {
    //   dispatch(setWarehouse({ type: "candles", data: currentData }));
    // } else {
    //   dispatch(setWarehouse({ type: type, data: currentData }));
    // }
    dispatch(setWarehouse({ type: type, data: currentData }));
  };

  const loadCandles = async () => {
    const currentData = await getWarehouseByType(axiosPrivate, "candles");
    if (type === "filters") {
      dispatch(setWarehouse({ type: "candles", data: currentData }));
    }
  };

  const loadOIVSubstances = async () => {
    const oivSubstances = await getOIVSubstances(axiosPrivate);
    dispatch(setWarehouse({ type: "oiv", data: oivSubstances }));
  };

  useEffect(() => {
    if (["filters", "stock", "treatments"].includes(type)) {
      getLastActiveTab();
      // default value
      // if (!activeTab) setActiveTab(type === "filters" ? "carton" : "treatments");
    }
    if (type === "treatments" && ["stock", "treatments"].includes(activeTab)) {
      setIsStock(activeTab === "stock");
      
      if (!substances || substances.length === 0) loadSubstances();

      if (type === "treatments" && activeTab === "treatments")
        loadOIVSubstances();
    }
    if (
      ["caps", "bottles", "filters", "machineries"].includes(type) &&
      (!warehouseData || warehouseData.length === 0)
    ) {
      loadWarehouse();

      if (type === "filters" && (!candlesData || candlesData.length === 0)) {
        loadCandles();
      }
    }
  }, [type, activeTab]);

  const setModifyMode = (row_id) => {
    setIsModifyMode(row_id);
  };

  const renderHeader = () => {
    return (
      <div className="primary_container_menu">
        <h2>
          {getLabel("warehouse", {
            type: (type === "machineries"
              ? getLabel("warehouseMachineriesNavLink")
              : type === "filters"
              ? getLabel("warehouseFiltersNavLink")
              : type === "bottles"
              ? getLabel("warehouseBottlesNavLink")
              : type === "caps"
              ? getLabel("warehouseCapsNavLink")
              : type === "stock"
              ? getLabel("warehouseStockNavLink")
              : type === "treatments"
              ? getLabel("warehouseTreatmentNavLink")
              : ""
            )?.toLowerCase(),
          })}
        </h2>
      </div>
    );
  };

  const loadUnits = async () => {
    const currentUnits = await getUnits(axiosPrivate);
    dispatch(setUnits(currentUnits));
  };

  useEffect(() => {
    if (!units || units.length === 0) {
      loadUnits();
    }
  }, []);

  useEffect(() => {
    if (!substances || substances?.length === 0) return;
    const tmp = {};
    tmp.tot = substances?.length || 0;
    const additive = substances?.filter((t) => t?.type === "ADDITIVE");
    const adjuvant = substances?.filter((t) => t?.type === "ADJUVANT");
    const detergent = substances?.filter((t) => t?.type === "DETERGENT");
    tmp.additive = additive?.length;
    tmp.adjuvant = adjuvant?.length;
    tmp.detergent = detergent?.length;

    setSubstancesLen(tmp);
  }, [substances]);

  const columnHelper = createColumnHelper();

  const warehouseColumns = useMemo(() => {
    if (type === "machineries") {
      return [
        columnHelper.accessor("name", {
          header: getLabel("machineryName"),
          cell: TableCell,
          meta: { filterVariant: "text" },
        }),
        columnHelper.accessor(
          (row) => (row?.type ? getLabel(`machineryType_${row?.type}`) : ""),
          {
            header: getLabel("machineryType"),
            cell: TableCell,
            meta: { isFilterDisabled: true },
          }
        ),
        columnHelper.accessor("notes", {
          header: getLabel("machineryNote"),
          cell: TableCell,
          meta: { isFilterDisabled: true },
        }),
        columnHelper.display({
          header: "",
          id: "edit",
          cell: EditCell,
          meta: { isEditable: role === ROLES.CLIENT, role },
        }),
      ];
    } else if (type === "filters") {

      const getFilterColumns = () => {
        const base = baseColumns();
        const filterType = activeTab?.toUpperCase?.();
        let typeSpecific = [];

        if (filterType === "CARTON") {
          typeSpecific = cartonColumns();
        } else if (filterType === "TANGENTIAL") {
          typeSpecific = tangentialColumns();
        } else if (filterType === "HOUSING") {
          typeSpecific = housingColumns();
        }

        return [
          ...base,
          ...typeSpecific,
          columnHelper.display({
            header: "",
            id: "edit",
            cell: EditCell,
            meta: { isEditable: role === ROLES.CLIENT, role },
          }),
        ];
      };

      const baseColumns = () => {
        return [
          columnHelper.accessor(
            (row) =>
              (row?.filter_name || "") +
              (row?.serial_number ? ` (${row?.serial_number})` : ""),
            {
              header: getLabel("filterName"),
              cell: TableCell,
              meta: { filterVariant: "text" },
            }
          ),
          // columnHelper.accessor(
          //   (row) => (row?.type ? getLabel(`filterType_${row?.type}`) : ""),
          //   {
          //     header: getLabel("filterType"),
          //     cell: TableCell,
          //     meta: { isFilterDisabled: true },
          //   }
          // ),
          columnHelper.accessor(
            (row) =>
              row?.status ? getLabel(`filterStatus_${row?.status}`) : "",
            {
              header: getLabel("filterStatus"),
              cell: TableCell,
              // meta: { isFilterDisabled: true },
            }
          ),
        ];
      };

      const cartonColumns = () => [
        columnHelper.accessor((row) => (row?.porosity || "") + " µm", {
          header: getLabel("filterPorosity"),
          cell: TableCell,
        }),
        columnHelper.accessor(
          (row) => `${Number(row?.total_filtered_liters) || 0}${row?.max_capacity_liters ? '/' : ''}${Number(row?.max_capacity_liters) || ''} L`,
          {
            header: getLabel("filterTotalFiltered"),
            cell: TableCell,
            meta: { isFilterDisabled: true, additional: "filters" },
          }
        ),
        columnHelper.accessor(
          (row) =>
            row?.cost ? row?.cost + ' €' : "",
          {
            header: getLabel("drugBatchCost"),
            cell: TableCell,
            meta: { isFilterDisabled: true },
          }
        ),
      ];

      const tangentialColumns = () => [
        columnHelper.accessor((row) => (row?.porosity || "") + " µm", {
          header: getLabel("filterPorosity"),
          cell: TableCell,
        }),
        // columnHelper.accessor((row) => row?.element_count || 0, {
        //   header: getLabel("filterMembraneCount"),
        //   cell: TableCell,
        //   meta: { isFilterDisabled: true },
        // }),
        columnHelper.accessor((row) => `${Number(row?.total_filtered_liters) || 0} L`, {
          header: getLabel("filterTotalFiltered"),
          cell: TableCell,
          meta: { isFilterDisabled: true },
        }),
        // columnHelper.accessor((row) => `${Number(row?.total_filtered_liters) || 0}${row?.max_capacity_liters ? '/' : ''}${Number(row?.max_capacity_liters) || ''} L`, {
        //   header: getLabel("filterTotalFiltered"),
        //   cell: TableCell,
        //   meta: { isFilterDisabled: true },
        // }),
        // columnHelper.accessor(
        //   (row) =>
        //     row?.cost ? row?.cost + ' €' : "",
        //   {
        //     header: getLabel("drugBatchCost"),
        //     cell: TableCell,
        //     meta: { isFilterDisabled: true },
        //   }
        // ),
      ];

      const housingColumns = () => [
        columnHelper.accessor((row) => `x${row?.candles?.length || 0}`, {
          header: getLabel("filterCandleCnt"),
          cell: TableCell,
          meta: { isFilterDisabled: true },
        }),
        columnHelper.accessor("candle_serial", {
          header: getLabel("filterCandleSerials"),
          cell: MultipleRowsCell,
          meta: {
            array: "candles",
            selector: "serial_number",
            isFilterDisabled: true,
          },
        }),
        columnHelper.accessor("porosity", {
          header: getLabel("filterPorosity"),
          cell: MultipleRowsCell,
          meta: {
            array: "candles",
            selector: "porosity",
            isFilterDisabled: true,
          },
        }),
        columnHelper.accessor("candle_total", {
          header: getLabel("filterTotalFiltered"),
          cell: MultipleRowsCell,
          meta: {
            array: "candles",
            selector: "candle_total",
            isFilterDisabled: true,
          },
        }),
        columnHelper.accessor("candle_cost", {
          header: getLabel("filterHousingTotalCost"),
          cell: MultipleRowsCell,
          meta: {
            array: "candles",
            selector: "candle_cost",
            isFilterDisabled: true,
          },
        }),
      ];

      return getFilterColumns();
    }

    if (["caps", "bottles"].includes(type)) {
      return [
        columnHelper.accessor("name", {
          header: getLabel(type === "caps" ? "capName" : "bottleName"),
          cell: TableCell,
          meta: { filterVariant: "text" },
        }),
        columnHelper.accessor("origin", {
          header: getLabel("capOrigin"),
          cell: TableCell,
          meta: { isFilterDisabled: true },
        }),
        ...(type === "bottles"
          ? [
              columnHelper.accessor(
                (row) =>
                  `${getLabel(`bottleVolume_${row?.bottle_type}`)} (${getLabel(
                    `bottleVolume_${row?.bottle_type}_Vol`
                  )})`,
                {
                  header: getLabel("bottleCapacity"),
                  cell: TableCell,
                  meta: { isFilterDisabled: true },
                }
              ),
            ]
          : []),
        columnHelper.accessor("quantity", {
          header: getLabel(type === "caps" ? "capQuantity" : "bottleQuantity"),
          cell: TableCell,
          meta: { isFilterDisabled: true },
        }),
        columnHelper.accessor((row) => row?.notes || "", {
          header: getLabel("capNote"),
          cell: TableCell,
          meta: { isFilterDisabled: true },
        }),
        columnHelper.display({
          header: "",
          id: "edit",
          cell: EditCell,
          meta: { isEditable: role === ROLES.CLIENT, role },
        }),
      ];
    }

    if (type === "treatments" && activeTab === "stock") {
      return [
        columnHelper.accessor("label", {
          header: getLabel("treatmentBatchName"),
          cell: TableCell,
          meta: {
            filterVariant: "text",
          },
        }),
        columnHelper.accessor("batch_number", {
          header: getLabel("drugBatchNumber"),
          cell: MultipleRowsCell,
          meta: {
            array: "batches",
            selector: "batch_number",
            isFilterDisabled: true,
          },
        }),
        columnHelper.accessor("supplier", {
          header: getLabel("drugBatchSupplier"),
          cell: MultipleRowsCell,
          meta: {
            array: "batches",
            selector: "supplier",
            isFilterDisabled: true,
          },
        }),
        columnHelper.accessor("batch_quantity", {
          header: getLabel("drugBatchQuantity"),
          cell: MultipleRowsCell,
          meta: {
            array: "batches",
            selector: "batch_quantity",
            isFilterDisabled: true,
          },
        }),
        columnHelper.accessor("batch_unitary_cost", {
          header: getLabel("drugBatchCost"),
          cell: MultipleRowsCell,
          meta: {
            array: "batches",
            selector: "batch_unitary_cost",
            isFilterDisabled: true,
          },
        }),
        columnHelper.accessor("expire_date", {
          header: getLabel("drugExpireDate"),
          cell: MultipleRowsCell,
          meta: {
            array: "batches",
            selector: "expire_date",
            isFilterDisabled: true,
            additionalType: "expired",
            additionalWarningMessage: getLabel("drugBatchNearExpireDate"),
            additionalErrorMessage: getLabel("drugBatchExpired"),
          },
        }),
        columnHelper.display({
          header: "",
          id: "edit",
          cell: EditCell,
          meta: {
            isEditable: role === ROLES.CLIENT,
            role: role,
          },
        }),
      ];
    }

    if (type === "treatments" && activeTab === "treatments") {
      return [
        columnHelper.accessor("name", {
          header: getLabel("drugName"),
          cell: TableCell,
          meta: {
            filterVariant: "text",
          },
        }),
        // columnHelper.accessor(
        //   (row) =>
        //     row?.type ? getLabel(`drugType_${row.type?.toUpperCase()}`) : "",
        //   {
        //     header: getLabel("drugType"),
        //     cell: TableCell,
        //     meta: {
        //       isFilterDisabled: true,
        //     },
        //   }
        // ),
        columnHelper.accessor("type", {
          header: getLabel("drugType"),
          cell: MultipleRowsCell,
          meta: {
            array: "ingredients",
            selector: "type",
            isFilterDisabled: true,
          },
        }),
        // columnHelper.accessor(
        //   (row) => `${row?.quantity} ${row?.unit || ""}`,
        //   {
        //     header: getLabel("drugQuantity"),
        //     cell: TableCell,
        //     meta: {
        //       type: "number",
        //       editable: true,
        //       regExpPattern: INT_FLOAT_REGEX,
        //       isFilterDisabled: true,
        //       register: register,
        //     },
        //   }
        // ),
        columnHelper.accessor(
          (row) =>
            row?.recommended_quantity
              ? `${row?.recommended_quantity} ${row?.unit || ""}`
              : "",
          {
            header: getLabel("drugIdealQuantity"),
            cell: TableCell,
            meta: {
              isFilterDisabled: true,
            },
          }
        ),
        columnHelper.accessor(
          (row) =>
            `${row?.min_range ? String(row?.min_range) : "n/a"} - ${
              row?.max_range ? String(row?.max_range) : "n/a"
            } ${row?.unit || ""}`,
          {
            header: getLabel("substanceRangeAllowed"),
            cell: TableCell,
            meta: {
              type: "number",
              isFilterDisabled: true,
            },
          }
        ),
        // columnHelper.accessor(
        //   (row) =>
        //     quantities?.length > 0
        //       ? `${quantities[row?.index] || ""}` || ""
        //       : (row?.quantity || "") +
        //         (row?.quantity ? " " + (row?.unit || "")  : ""),
        //   {
        //     header: getLabel("drugQuantity"),
        //     cell: TableCell,
        //     meta: {
        //       isFilterDisabled: true,
        //     },
        //   }
        // ),
        columnHelper.accessor(
          (row) =>
            `${row?.min_temperature ? String(row?.min_temperature) : "n/a"} - ${
              row?.max_temperature ? String(row?.max_temperature) : "n/a"
            } \u00b0C` || "",
          {
            header: getLabel("substanceTemperature"),
            cell: TableCell,
            meta: {
              type: "number",
              isFilterDisabled: true,
            },
          }
        ),
        columnHelper.accessor(
          (row) =>
            `${row?.stocks ? row?.stocks : ""} ${
              row?.stocks
                ? getUnitOptions({ unit_id: row?.unit_id })?.[0]?.label
                : ""
            }`,
          {
            header: getLabel("drugStockQuantity"),
            cell: TableCell,
            meta: {
              isFilterDisabled: true,
              additional: "expired",
              additionalType: "min_stock",
              additionalWarningMessage: getLabel("drugBatchNearMinStock"),
              additionalErrorMessage: getLabel("drugBatchUnderMinStock"),
            },
          }
        ),
        // columnHelper.accessor((row) => `${row?.min_stock ? row?.min_stock : ''} ${row?.min_stock ? getUnitOptions({ unit_id: row?.unit_id})?.[0]?.label : ''}`, {
        //   header: getLabel("drugStockMin"),
        //   cell: TableCell,
        //   meta: {
        //     isFilterDisabled: true,
        //   },
        // }),
        columnHelper.display({
          header: "",
          id: "edit",
          cell: EditCell,
          meta: {
            isEditable: role === ROLES.CLIENT,
            role: role,
          },
        }),
      ];
    }

    return [];
  }, [type, activeTab, getLabel]);

  const candleColumn = [
    columnHelper.accessor(
      (row) => row?.serial_number || "",
      {
        header: getLabel("filterCandleSerial"),
        cell: TableCell,
        meta: { filterVariant: "text" },
      }
    ),
    columnHelper.accessor(
      (row) =>
        row?.status ? getLabel(`filterStatus_${row?.status}`) : "",
      {
        header: getLabel("filterStatus"),
        cell: TableCell,
        // meta: { isFilterDisabled: true },
      }
    ),
    columnHelper.accessor((row) => (row?.porosity || "") + " µm", {
      header: getLabel("filterPorosity"),
      cell: TableCell,
    }),
    columnHelper.accessor((row) => (row?.type ? getLabel(`filterCandleType_${row?.type}`) : ""), {
      header: getLabel("filterCandleType"),
      cell: TableCell,
    }),
    columnHelper.accessor(
      (row) => `${Number(row?.total_filtered_liters) || 0}${row?.max_capacity_liters ? '/' : ''}${Number(row?.max_capacity_liters) || ''} L`,
      {
        header: getLabel("filterTotalFiltered"),
        cell: TableCell,
        meta: { isFilterDisabled: true, additional: "filters" },
      }
    ),
    columnHelper.accessor(
      (row) =>
        row?.cost ? row?.cost + ' €' : "",
      {
        header: getLabel("drugBatchCost"),
        cell: TableCell,
        meta: { isFilterDisabled: true },
      }
    ),
    columnHelper.display({
      header: "",
      id: "edit",
      cell: EditCell,
      meta: { isEditable: role === ROLES.CLIENT, role },
    }),
  ];

  const renderDrugsNavMenu = () => {
    return (
      <div className="cards_wrapper">
        <div>
          {!isStock && (
            <h5 className="tanks_title">
              {getLabel("numTotSubstances")}
              <span className="tanks_span">{substancesLen?.tot || "0"}</span>
            </h5>
          )}
        </div>
      </div>
    );
  };

  const customIdInvalidIngredientsSum = "custom-id-invalid-ingredients-sum";

  const submitTreatmentForm = async () => {
    if (Object.keys(errors).length > 0) {
      return;
    }

    const data = getValues();

    const id = data?.id || isRemoveId;

    const drugCompositions = [];
    const compositionsData =
      (data?.is_co_formulated === false // only 1st element in case of !coFormulated
        ? [data?.compositions?.[0]]
        : data?.compositions) || [];

    for (const c of compositionsData) {
      const tmpComposition = {};
      if (c?.type) tmpComposition.type = c?.type?.value;
      if (c?.functional) tmpComposition.functional = c?.functional?.value;
      if (compositionsData?.length === 1) tmpComposition.quantity = 100;
      else if (c?.quantity)
        tmpComposition.quantity =
          data?.is_co_formulated === true ? Number(c?.quantity) : 100;
      if (c?.substance?.value) tmpComposition.substance = c?.substance?.value;
      if (c?.physical?.value && c?.substance?.value !== "Woods")
        tmpComposition.physical = c?.physical?.value;
      if (Array.isArray(c?.origins) && c?.origins?.length > 0)
        tmpComposition.origins =
          c?.origins?.map((o) => o?.value)?.filter(Boolean) || [];
      drugCompositions.push(tmpComposition);
    }

    if (isRemoveId === -1 && drugCompositions?.length > 0) {
      const ingredientsSum = drugCompositions?.reduce((acc, curr) => {
        return acc + (Number(curr?.quantity) || 0);
      }, 0);

      if (ingredientsSum <= 0 || ingredientsSum > 100) {
        toast.error(
          getLabel("toast_invalidIngredientsSum", { value: ingredientsSum }),
          {
            toastId: customIdInvalidIngredientsSum,
            position: toast.POSITION.BOTTOM_RIGHT,
            exclude: true,
            autoClose: 5000,
          }
        );
        return;
      } else if (ingredientsSum < 100) {
        toast.warning(
          getLabel("toast_invalidIngredientsSumWarning", {
            value: ingredientsSum,
          }),
          {
            toastId: customIdInvalidIngredientsSum,
            position: toast.POSITION.BOTTOM_RIGHT,
            exclude: true,
            autoClose: 5000,
          }
        );
        return;
      }
    }

    const requestPayload = {
      ...data,
      ...((data?.unit_id && {
        unit_id: data?.unit_id?.value,
        unit: data?.unit_id?.label,
      }) ||
        0),
      ...((data?.type && { type: data?.type.value }) || ""),
      ...(id && { id: Number(id) }),
      ingredients: drugCompositions || [],
    };

    console.log("requestPayload", requestPayload);
    
    const toastId = toast.loading(getLabel("toast_inProgress"), {
      type: toast.TYPE.INFO,
      position: toast.POSITION.BOTTOM_RIGHT,
      exclude: true,
    });

    const response = await (isModifyMode > 0
      ? updateDrug(id, requestPayload, axiosPrivate)
      : isRemoveId > 0
      ? deleteDrug(id, axiosPrivate)
      : newDrug(requestPayload, axiosPrivate));

    toast.update(toastId, {
      render: response?.success
        ? getLabel(response?.success, { name: data?.name || "" })
        : getLabel(response?.error),
      type:
        !response || response?.error ? toast.TYPE.ERROR : toast.TYPE.SUCCESS,
      isLoading: false,
      position: toast.POSITION.BOTTOM_RIGHT,
      autoClose: 4000,
    });
    console.log('response', response)
    if (response && response?.success) {
      if (isRemoveId > 0) {
        removeSubstanceItem(response);
        setIsRemoveId(-1);
      } else if (isModifyMode > 0) {
        updateSubstanceItem(response);
        setIsModifyMode(-1);
      } else addSubstanceItem(response);

      handleCloseModal(type);
    }
  };

  const addSubstanceItem = (item) => {
    dispatch(addDrugItem(item.data));
  };

  const removeSubstanceItem = (item) => {
    dispatch(removeDrugItem({ id: item?.data?.id }));
  };

  const updateSubstanceItem = (item) => {
    if (!item?.data?.id) {
      console.error("Missing item ID");
      return;
    }
    dispatch(
      updateDrugItem({
        id: item.data.id,
        data: item.data,
      })
    );
  };

  const submitCapForm = async () => {
    if (Object.keys(errors).length > 0) {
      return;
    }
    const data = getValues();

    const requestPayload = {
      type: type,
    };

    if (type === "caps" || type === "bottles") {
      requestPayload.name = data?.new_warehouse_name;
      requestPayload.quantity = data?.new_warehouse_quantity;
      requestPayload.bottle_type =
        data?.new_warehouse_bottles_capacity?.label?.toUpperCase();
      requestPayload.origin = data?.new_warehouse_origin;
      requestPayload.notes = data?.new_warehouse_notes;
    } else if (type === "machineries") {
      requestPayload.name = data?.new_warehouse_name;
      requestPayload.machinery_type = data?.new_warehouse_machinery_type?.value;
      requestPayload.notes = data?.new_warehouse_notes;
    } else if (type === "filters") {
      if (isStock) {
        requestPayload.candle_type = data?.type?.value;
        requestPayload.candle_id = data?.candle_id;
        requestPayload.subtype = "CANDLE";
      } else {
        requestPayload.subtype = activeTab?.toUpperCase?.();
        requestPayload.element_count = (activeTab === "tangential"
          ? Number(data?.membrane_count) 
          : ((data?.candles?.filter(c => c?.serial_number) || [])?.length || 0)) || 0;
      } 
      requestPayload.name = data?.new_filter_name;
      requestPayload.serial_number = data?.serial_number;
      requestPayload.porosity = data?.porosity?.value || null;
      requestPayload.max_filter_capacity = Number(data?.max_filter_capacity) || 0;
      requestPayload.cost = Number(data?.cost) || 0;
      if (activeTab === "housing" && !isStock) {
        requestPayload.candles = data?.candles?.map(c => c?.id) || [];
        requestPayload.porosity = data?.candles?.[0]?.porosity?.value || null;
      }
      requestPayload.notes = data?.new_filter_notes;
    }
    console.log("requestPayload", requestPayload);
    // return;

    const toastId = toast.loading(getLabel("toast_inProgress"), {
      type: toast.TYPE.INFO,
      position: toast.POSITION.BOTTOM_RIGHT,
      exclude: true,
    });

    const response = await (isModifyMode > 0
      ? updateWarehouseByType(type, isModifyMode, requestPayload, axiosPrivate)
      : isRemoveId > 0
      ? deleteWarehouseByType(type, isRemoveId, axiosPrivate)
      : newWarehouseByType(type, requestPayload, axiosPrivate));

    toast.update(toastId, {
      render: response?.success
        ? getLabel(response?.success, { type: type || "" })
        : getLabel(response?.error),
      type:
        !response || response?.error ? toast.TYPE.ERROR : toast.TYPE.SUCCESS,
      isLoading: false,
      position: toast.POSITION.BOTTOM_RIGHT,
      autoClose: 4000,
    });
    console.log('response', response)
    if (response && response?.success) {
      if (isRemoveId > 0) {
        removeWarehouseItem(type, response);
        setIsRemoveId(-1);
      } else if (isModifyMode > 0) {
        updateWarehouseItem(response);
        setIsModifyMode(-1);
      } else addWarehouseItem(type, response);

      handleCloseModal(type);
    }
  };

  const addWarehouseItem = (type, item) => {
    const warehouseDataType = type === "filters" && isStock ? "candles" : type;
    dispatch(addWarehouse({ warehouseDataType, data: item?.data }));
  };

  const removeWarehouseItem = (type, item) => {
    dispatch(removeWarehouse({ type, id: item?.data?.id }));
  };

  const updateWarehouseItem = (item) => {
    if (!item?.data?.id) {
      console.error("Missing item ID");
      return;
    }
    const warehouseDataType = type === "filters" && isStock ? "candles" : type;
    dispatch(
      updateWarehouse({
        type: warehouseDataType, // This should match "caps", "treatments", etc.
        id: item.data.id,
        data: item.data, // The actual updated data
      })
    );

    if (warehouseDataType === "candles" && item.filter?.id) {
      //  update the housing filter using it
      dispatch(
        updateWarehouse({
          type: "filters",
          id: item.filter.id,
          data: item.filter,
        })
      );
    }

  };

  const updateWarehouseStockItem = (item) => {
    if (!item?.data?.id) {
      console.error("Missing item ID");
      return;
    }

    dispatch(
      updateDrugItem({
        id: item.data.id,
        data: item.data,
      })
    );
  };

  const removeRow = (id) => {
    setIsRemoveId(id);
    setIsModalOpen(true);
  };

  const submitStockForm = async () => {
    if (Object.keys(errors).length > 0) {
      return;
    }

    const data = getValues();

    const id = data?.id;
    const batchesData = batches?.map((i) => {
      return {
        ...i,
        stock: Number(i?.stock),
        cost: Number(Number(i?.cost)?.toFixed(2)),
      };
    });
    const requestPayload = {
      batches: batchesData,
      ...(id && { id: Number(id) }),
    };

    // console.log("requestPayload", requestPayload);

    const toastId = toast.loading(getLabel("toast_inProgress"), {
      type: toast.TYPE.INFO,
      position: toast.POSITION.BOTTOM_RIGHT,
      exclude: true,
    });
    const response = await updateDrugWarehouse(
      id,
      requestPayload,
      axiosPrivate
    );

    console.log(response);
    toast.update(toastId, {
      render: response?.success
        ? getLabel(response?.success, { name: data?.name || "" })
        : getLabel(response?.error),
      type:
        !response || response?.error ? toast.TYPE.ERROR : toast.TYPE.SUCCESS,
      isLoading: false,
      position: toast.POSITION.BOTTOM_RIGHT,
      autoClose: 4000,
    });

    if (response && response?.success) {
      updateWarehouseStockItem(response);
      handleCloseModal(type);
    }
  };

  const handleCloseModal = (type) => {
    if (
      ["caps", "bottles", "treatments", "stock", "machineries", "filters"].includes(type)
    )
      setIsModalOpen(false);

    if (type === "filters") setIsStock(false);

    if (isRemoveId > 0) setIsRemoveId(-1);
    if (isModifyMode > 0) setIsModifyMode(-1);
  };

  const handleOpenCandleModal = (modifyModeId = -1) => {
    setIsStock(true); // trick to reuse isStock for candles
    handleOpenModal(modifyModeId, true);
  };

  const handleOpenModal = (modifyModeId = -1, forceIsStock = null) => {
    clearErrors();
    setModifyMode(modifyModeId);

    if (type === "caps" || type === "bottles" || type === "machineries") {
      if (modifyModeId === -1) {
        reset({
          id: "",
          new_warehouse_name: "",
          new_warehouse_quantity: "",
          ...(type === "bottles" && { new_warehouse_bottles_capacity: "" }),
          ...(type === "machineries" && { new_warehouse_machinery_type: "" }),
          new_warehouse_origin: "",
          new_warehouse_notes: "",
        });
      } else if (modifyModeId > 0) {
        const c = warehouseData?.find((cc) => cc.id === modifyModeId) || {};
        setValue("id", c?.id);
        setValue("new_warehouse_name", c?.name);
        setValue("new_warehouse_quantity", c?.quantity);
        if (c?.bottle_type) {
          const translated = c?.bottle_type
            ? getLabel(`bottleVolume_${c?.bottle_type}`)
            : "";
          const translatedVol = c?.bottle_type
            ? getLabel(`bottleVolume_${c?.bottle_type}_Vol`)
            : "";
          setValue("new_warehouse_bottles_capacity", {
            value: type,
            label: translated,
            name: `${translated} (${translatedVol})`,
          });
        }
        if (type === "machineries") {
          const translated = c?.type
            ? getLabel(`machineryType_${c?.type}`)
            : "";
          setValue("new_warehouse_machinery_type", {
            value: c?.type,
            label: translated,
          });
        }

        setValue("new_warehouse_notes", c?.notes);
        setValue("new_warehouse_origin", c?.origin);
      }
    } else if (type === "treatments") {
      if (modifyModeId === -1) {
        reset({
          id: "",
          name: "",
          min_range: "",
          max_range: "",
          min_temperature: "",
          max_temperature: "",
          type: "",
          is_co_formulated: false,
          compositions: [
            {
              type: { value: null, label: "" },
              functional: { value: null, label: "" },
              substance: { value: null, label: "" },
              physical: { value: null, label: "" },
              origins: [],
              quantity: 0,
            },
          ],
        });
      } else if (modifyModeId > 0) {
        const s = substances?.find((cc) => cc.id === modifyModeId) || {};
        setValue("id", s?.id);
        setValue("name", s?.name);
        setValue("min_range", s?.min_range);
        setValue("max_range", s?.max_range);
        setValue("min_temperature", s?.min_temperature);
        setValue("max_temperature", s?.max_temperature);
        setValue("quantity", s?.quantity);
        setValue("unit_id", {
          value: s?.unit_id,
          label: s?.unit || units?.find((u) => u?.id === s?.unit_id)?.unit,
        });
        setValue("type", {
          value: s?.type,
          label: getLabel(`drugType_${s?.type}`),
        });
        const compositionData = [];
        for (const c of s?.ingredients) {
          const tmpComposition = {};
          if (c?.type)
            tmpComposition.type = {
              value: c?.type,
              label: getLabel(`drugType_${c?.type}`),
            };
          if (c?.functional)
            tmpComposition.functional = {
              value: c?.functional,
              label: getLabel(`drugLabelGroup_${c?.functional}`),
            };
          if (c?.quantity)
            tmpComposition.quantity =
              s?.ingredients?.length > 1 ? Number(c?.quantity) : 100;
          if (c?.substance)
            tmpComposition.substance = {
              value: c?.substance,
              label: getLabel(`drugLabel_${c?.substance}`),
            };
          if (c?.physical && c?.substance !== "Woods")
            tmpComposition.physical = {
              value: c?.physical,
              label: getLabel(`drugLabel_${c?.physical}`),
            };
          if (Array.isArray(c?.origins) && c?.origins?.length > 0)
            tmpComposition.origins =
              c?.origins?.map((o) => {
                return { value: o, label: getLabel(`drugLabel_${o}`) };
              }) || [];
          compositionData.push(tmpComposition);
        }

        setValue("is_co_formulated", s?.is_co_formulated || false);
        setValue(
          "compositions",
          compositionData?.length > 0
            ? compositionData
            : [
                {
                  type: { value: null, label: "" },
                  functional: { value: null, label: "" },
                  substance: { value: null, label: "" },
                  physical: { value: null, label: "" },
                  origins: [],
                  quantity: 0,
                },
              ]
        );
      }
    } else if (type === "stock") {
      if (modifyModeId === -1) {
        reset({
          id: "",
          name: "",
          min_stock: null,
          unit: { label: "", value: "" },
          batches: [],
        });
      } else if (modifyModeId > 0) {
        const s = substances?.find((cc) => cc.id === modifyModeId) || {};

        setValue("id", s?.id || "");
        setValue("name", s?.name || "");
        setValue("unit_id", {
          label: s?.unit,
          value: s?.unit_id,
        });
        setValue("type", {
          label: getLabel(`drugType_${s?.type}`),
          value: s?.type,
        });
        setValue("min_range", s?.min_range || "");
        setValue("max_range", s?.max_range || "");
        setValue("min_temperature", s?.min_temperature || "");
        setValue("max_temperature", s?.max_temperature || "");
        setValue("notes", s?.notes || "");
        setValue("min_stock", s?.min_stock);

        const unit_options = getUnitOptions(s);

        if (unit_options?.length === 1) setValue("unit", unit_options?.[0]);
        else setValue("unit", { label: "", value: "" });
        const batches = s?.batches || [
          { batchesData: { value: null, label: null } },
        ];
        setValue("batches", batches);
      }
    } else if (type === "filters") {
      const finalIsStock = forceIsStock ?? isStock;
    
      if (modifyModeId === -1) {
        if (activeTab === "housing" && finalIsStock) {
          // candle
          reset({
            candle_id: "",
            serial_number: "",
            porosity: { label: "", value: "" },
            type: { label: "", value: "" },
            status: "",
            cost: "",
            max_filter_capacity: { label: "", value: "" },
            total_filtered_liters: 0,
          });
        } else {
          // filter
          reset({
            id: "",
            new_filter_name: "",
            serial_number: "",
            porosity: { label: "", value: "" },
            max_filter_capacity: 0,
            candles: [{ value: null, label: null }]
          });
        }
      } else if (modifyModeId > 0) {
        if (activeTab === "housing" && finalIsStock) {
          const c = candlesData?.find((cc) => cc.id === modifyModeId) || {};
          
          setValue("candle_id", c?.id);
          setValue("serial_number", c?.serial_number);
          setValue("porosity", { label: getLabel(`filterPorosity_${parseFloat(c?.porosity).toFixed(2)}`), value: c?.porosity });
          setValue("type", { label: getLabel(`filterCandleType_${c?.type}`), value: c?.type });
          setValue("status", c?.status);
          setValue("max_filter_capacity", c?.max_capacity_liters);
          setValue("total_filtered_liters", c?.total_filtered_liters);
          setValue("cost", c?.cost);
        } else {
          const f = warehouseData?.find((cc) => cc.id === modifyModeId) || {};
          
          setValue("id", f?.id);
          setValue("new_filter_name", f?.filter_name);
          setValue("serial_number", f?.serial_number);
          
          let porosityLabel = null;
          switch (Number(f?.porosity)) {
            case 0.1: porosityLabel = getLabel("filterPorosity_ULTRA"); break;
            case 0.2: porosityLabel = getLabel("filterPorosity_STERILE"); break;
            case 0.45: porosityLabel = getLabel("filterPorosity_FINE"); break;
            case 0.65: porosityLabel = getLabel("filterPorosity_MEDIUM"); break;
            case 0.8: porosityLabel = getLabel("filterPorosity_COARSE"); break;
            case 1.2: porosityLabel = getLabel("filterPorosity_VERY_COARSE"); break;
          }
          if (porosityLabel) setValue("porosity", { label: porosityLabel, value: f?.porosity });
          setValue("max_filter_capacity", Number(f?.max_capacity_liters));
          setValue("new_filter_notes", f?.notes);
          setValue("cost", f?.cost);
          
          if (activeTab === 'housing') {
            const candlesArray = [];
            if ((Array.isArray(f?.candles) && f?.candles?.length > 0)) {
              candlesArray.push(...((f?.candles?.map(c => { return { 
                ...c, 
                is_disabled: c?.status !== "ACTIVE" || c?.filter_id,
                value: c?.id, 
                label: `${c?.serial_number} ${c?.porosity || '-'}µm [${c?.porosity ? getLabel(`filterCandleType_${c?.type}_Short`)?.toLowerCase?.() : ''}]`
              }})
            ) || []));
            } else candlesArray.push({ value: null, label: null });
            
            setValue("candles", candlesArray);
          }
        }
      }
    }
    setIsModalOpen(true);
  };

  const getUnitOptions = (substance) => {
    const unit_id = substance?.unit_id || null;
    if (!unit_id) return [];

    const options = [
      {
        value: "3",
        label: getLabel("drugBatchUnit_L"),
      },
      {
        value: "4",
        label: getLabel("drugBatchUnit_Kg"),
      },
      {
        value: "5",
        label: getLabel("drugBatchUnit_Unit"),
      },
    ];

    switch (unit_id) {
      case 1:
      case 2:
      case 4:
      case 6:
      case 7:
        // Kg
        return [options[1]];
      case 3:
        // L
        return [options[0]];
      case 5:
        // unit
        return [options[2]];
    }

    return [];
  };

  const getCandles = () => {
    return candlesData
      ?.map(c => {
        return { ...c, opType: type?.toUpperCase() };
      }) || [];
  }
  
  const getAdditionalData = () => {
    const data = (type === "filters" && activeTab === "housing" && !isStock) ? candlesData : warehouseData;
    return data?.filter((c) =>
      isModifyMode > 0
        ? c?.id !== isModifyMode
        : isRemoveId > 0
        ? c?.id === isRemoveId
        : true
    ) || [];
  }

  if (
    ![
      "stock",
      "treatments",
      "caps",
      "bottles",
      "stock",
      "filters",
      "machineries",
    ].includes(type)
  )
    return;

  return (
    <div className="primary_container">
      <SecondaryBar
        breadCrumb={[
          getLabel("warehouseNavLink"),
          type === "machineries"
            ? getLabel("warehouseMachineriesNavLink")
            : type === "filters"
            ? getLabel("warehouseFiltersNavLink")
            : type === "bottles"
            ? getLabel("warehouseBottlesNavLink")
            : type === "caps"
            ? getLabel("warehouseCapsNavLink")
            : type === "stock"
            ? getLabel("warehouseStockNavLink")
            : type === "treatments"
            ? getLabel("warehouseTreatmentNavLink")
            : "",
        ]}
        isBasePathNeeded={true}
      />
      {renderHeader()}
      <div className="warehouse_wrapper">
        {/* {type === "stock" && ( */}
        {type === "treatments" && isStock && (
          <>
            <TabSelector
              tabs={tabs}
              onTabChange={handleTabChange}
              activeTab={activeTab}
            />
            {role === ROLES.CLIENT && (
              <div className="confirmation_wrapper">
                <ConfirmationModal
                  isOpen={isModalOpen}
                  onConfirm={submitStockForm}
                  onClose={() => handleCloseModal(type)}
                  description={getLabel("modalUpdateStockDescription", {
                    name: getValues("name") || "",
                  })}
                  // description={getLabel("modalUpdateStockDescription", {
                  //   name:
                  //     substances?.find((s) =>
                  //       isModifyMode > 0
                  //         ? s?.id !== isModifyMode
                  //         : isRemoveId > 0
                  //         ? s?.id === isRemoveId
                  //         : true
                  //     )?.name || "",
                  // })}
                  isOperationRecap={true}
                  totalStep={0}
                  errors={errors}
                  getValues={getValues}
                  // configs={configs}
                >
                  <OperationRecap
                    type={
                      isModifyMode > 0
                        ? "NEW_STOCK"
                        : isRemoveId > 0
                        ? "REMOVE_STOCK"
                        : "NEW_STOCK"
                    }
                    control={control}
                    register={register}
                    getValues={getValues}
                    errors={errors}
                    units={units}
                    configs={configs}
                    arrayFields={arrayFields2}
                    additionalType={type}
                    additionalData={substances?.filter((s) =>
                      isModifyMode > 0
                        ? s?.id !== isModifyMode
                        : isRemoveId > 0
                        ? s?.id === isRemoveId
                        : true
                    )}
                  />
                </ConfirmationModal>
              </div>
            )}
            {/* <div className={`tanks_wrapper`}>
              <div className={`tanks_recap ${isStock ? "no_min_width mb-40" : ""}`}>
                <div className="tanks_recap_wrapper">
                  <Tooltip
                    variant={"info"}
                    html={getLabel("drugWarehouseProductPage")}
                    place="top"
                    events={["hover"]}
                  >
                    <CTA
                      customClassName={"complete"}
                      onClick={() => navigate('/warehouse/treatments')}
                    >
                      <FontAwesomeIcon icon={"fa-tablets"} />
                    </CTA>
                  </Tooltip>
                </div>
              </div>
            </div> */}
            <Suspense fallback={<SkeletonTableEdit colCount={3} />}>
              <TableEdit
                columns={warehouseColumns}
                defaultData={substances?.map((t) => {
                  return { ...t, opType: type?.toUpperCase() };
                })}
                type={type}
                isMobile={isMobile}
                isEditable={role === ROLES.CLIENT}
                setValue={setValue}
                control={control}
                removeRowHandler={removeRow}
                modalHandler={handleOpenModal}
                setModifyMode={setModifyMode}
                rowClickHandler={(row) => {
                  navigate(`/warehouse/treatment/${row.original.id}`);
                }}
              />
            </Suspense>
          </>
        )}
        {type === "treatments" && !isStock && (
          <>
            <TabSelector
              tabs={tabs}
              onTabChange={handleTabChange}
              activeTab={activeTab}
            />
            {renderDrugsNavMenu()}
            {role === ROLES.CLIENT && (
              <div className="confirmation_wrapper">
                <ConfirmationModal
                  isOpen={isModalOpen}
                  onConfirm={submitTreatmentForm}
                  onClose={() => handleCloseModal(type)}
                  description={getLabel(
                    isModifyMode > 0
                      ? "modalUpdateTreatmentDescription"
                      : isRemoveId > 0
                      ? "modalRemoveTreatmentDescription"
                      : "modalNewTreatmentDescription"
                  )}
                  isOperationRecap={true}
                  totalStep={0}
                  errors={errors}
                  getValues={getValues}
                  // configs={configs}
                >
                  <OperationRecap
                    type={
                      isModifyMode > 0
                        ? "NEW_TREATMENT"
                        : isRemoveId > 0
                        ? "REMOVE_TREATMENT"
                        : "NEW_TREATMENT"
                    }
                    control={control}
                    register={register}
                    getValues={getValues}
                    setValue={setValue}
                    errors={errors}
                    units={units}
                    arrayFields={arrayFields}
                    additionalType={type}
                    additionalData={substances?.filter((s) =>
                      isModifyMode > 0
                        ? s?.id !== isModifyMode
                        : isRemoveId > 0
                        ? s?.id === isRemoveId
                        : true
                    )}
                  />
                </ConfirmationModal>
              </div>
            )}
            <Suspense fallback={<SkeletonTableEdit colCount={3} />}>
              <TableEdit
                columns={warehouseColumns}
                defaultData={substances?.map((t) => {
                  return { ...t, opType: type?.toUpperCase() };
                })}
                type={type}
                isMobile={isMobile}
                isEditable={role === ROLES.CLIENT}
                setValue={setValue}
                removeRowHandler={removeRow}
                modalHandler={handleOpenModal}
                setModifyMode={setModifyMode}
                rowClickHandler={(row) => {
                  navigate(`/warehouse/treatment/${row.original.id}`);
                }}
              />
            </Suspense>
          </>
        )}
        {["caps", "bottles", "filters", "machineries"].includes(type) && (
          <>
            {type === "filters" && <TabSelector
              tabs={filterTabs}
              onTabChange={handleTabChange}
              activeTab={activeTab}
            />}
            {role === ROLES.CLIENT && (
              <div className="confirmation_wrapper">
                <ConfirmationModal
                  isOpen={isModalOpen}
                  onConfirm={submitCapForm}
                  onClose={() => handleCloseModal(type)}
                  description={getLabel(
                    isModifyMode > 0
                      ? type === "bottles"
                        ? "modalUpdateBottleDescription"
                        : type === "filters" ? isStock ? "modalUpdateCandleDescription"
                        : "modalUpdateFilterDescription" : "modalUpdateCapDescription"
                      : isRemoveId > 0
                      ? type === "bottles"
                        ? "modalRemoveBottleDescription"
                        : type === "filters" ? isStock ? "modalRemoveCandleDescription"
                        : "modalRemoveFilterDescription" : "modalRemoveCapDescription"
                      : type === "bottles"
                      ? "modalNewBottleDescription"
                      : type === "filters" ? isStock ? "modalNewCandleDescription"
                        : "modalNewFilterDescription" : "modalNewCapDescription",
                    { type: (type === "filters" && ["carton", "tangential", "housing"].includes(activeTab)) ? getLabel(`filterType_${activeTab?.toUpperCase?.()}`)?.toLowerCase?.() : ""})}
                  isOperationRecap={true}
                  totalStep={0}
                  errors={errors}
                  getValues={getValues}
                  // configs={configs}
                >
                  <OperationRecap
                    type={
                      isModifyMode > 0
                        ? "NEW_WAREHOUSE"
                        : isRemoveId > 0
                        ? "REMOVE_WAREHOUSE"
                        : "NEW_WAREHOUSE"
                    }
                    control={control}
                    register={register}
                    getValues={getValues}
                    errors={errors}
                    arrayFields={type === "filters" ? arrayFields3 : []}
                    additionalType={type}
                    additionalSubtype={type === "filters" ? (activeTab === "housing" && isStock ? "candle" : activeTab) : null}
                    additionalData={getAdditionalData()}
                  />
                </ConfirmationModal>
              </div>
            )}
            {type === "filters" && activeTab === "housing" && <h4>{getLabel("filterHousing")}</h4>}
            {type === "filters" && activeTab === "housing" && candlesData?.length === 0 && (
              <h5 className="alert_no_info">{getLabel("noCandles")}</h5>
            )}
            {(type !== "filters" || (activeTab !== "housing" || (activeTab === "housing" && candlesData?.length > 0))) && <Suspense fallback={<SkeletonTableEdit colCount={3} />}>
              <TableEdit
                columns={warehouseColumns}
                defaultData={warehouseData
                  ?.filter(c =>
                    type !== "filters" || (c?.type === activeTab?.toUpperCase())
                  )
                  ?.map(c => {
                    return { ...c, opType: type?.toUpperCase() };
                  }
                )}
                type={type}
                isMobile={isMobile}
                isEditable={role === ROLES.CLIENT}
                setValue={setValue}
                removeRowHandler={removeRow}
                modalHandler={handleOpenModal}
                setModifyMode={setModifyMode}
                isLegend={false}
              />
            </Suspense>}

            {type === "filters" && activeTab === "housing" && <h4>{getLabel("filterCandles")}</h4>}
            {type === "filters" && activeTab === "housing" && <Suspense fallback={<SkeletonTableEdit colCount={3} />}>
              <TableEdit
                columns={candleColumn}
                defaultData={getCandles()}
                type={"filters"}
                isMobile={isMobile}
                isEditable={role === ROLES.CLIENT}
                setValue={setValue}
                removeRowHandler={removeRow}
                modalHandler={handleOpenCandleModal}
                setModifyMode={setModifyMode}
                isLegend={false}
              />
            </Suspense>}
          </>
        )}
      </div>
    </div>
  );
};

export default Warehouse;
